import type { DocumentNode, FragmentDefinitionNode } from 'graphql';
import { isNull } from 'lodash';
import { useFragment } from '@frameio/graphql-client';
import type { AccountPermissions } from '@frameio/graphql-client/generated';
import {
  CanAddPaidUserPermissionForAccount,
  CanAssignAdminPermissionForAccount,
  CanAssignOwnerPermissionForAccount,
  CanCreateWorkspacePermissionForAccount,
  CanDeleteUserPermissionForAccount,
  CanManageAllPermissionForAccount,
  CanManageBillingPermissionForAccount,
  CanManageResourceControlPoliciesForAccount,
  CanUpdateUserStatusPermissionForAccount,
  CanViewAllUsersPermissionForAccount,
  CanViewUserGroupPermissionForAccount,
  IsMountedStorageFeatureDisabledForAccount,
  IsTranscriptionFeatureDisabledForAccount,
  IsUploadLinksFeatureDisabledForAccount,
} from '@frameio/graphql-client/generated';

// Map of feature access checks to their corresponding disabled feature names
const DISABLED_FEATURE_MAP = {
  canAccessMountedStorageFeature: 'mountedStorage',
  canAccessTranscriptionFeature: 'transcription',
  canAccessUploadLinksFeature: 'uploadLinks',
} as const;

type DisabledFeatureKey = keyof typeof DISABLED_FEATURE_MAP;
type RegularPermissionKey = keyof Omit<AccountPermissions, '__typename'>;
export type AccountPermissionsCheck = RegularPermissionKey | DisabledFeatureKey;

type UseAccountPermissionsArgs = {
  /** The id of the Account to check against */
  accountId: string;
  /** The permission field to check */
  check: AccountPermissionsCheck;
  /** Whether to consider the permission true initially prior to data resolving */
  isInitiallyPermitted?: boolean;
};

/**
 * Checks if a disabled feature is accessible based on the account's disabled features
 * @param check - The feature access check being performed
 * @param result - The fragment result containing disabled features data
 * @returns A boolean indicating whether the feature is accessible
 */
export const checkDisabledFeatureAccess = (
  check: DisabledFeatureKey,
  result: { data?: { disabledFeatures?: Record<string, boolean> } }
): boolean => {
  const disabledFeatureName = DISABLED_FEATURE_MAP[check];
  // Return true if the feature is not disabled or if the disabled state is undefined; otherwise, return false.
  const isFeatureDisabled =
    result.data?.disabledFeatures?.[disabledFeatureName];
  return !isFeatureDisabled;
};

/**
 * Reactive hook to read Account-level permissions directly off the cache
 * @param {accountId, check, isInitiallyPermitted}
 * @returns A boolean of whether a given check for the Account with the given `accountId` is true
 */
export const useAccountPermissions = ({
  accountId,
  check,
  isInitiallyPermitted = false,
}: UseAccountPermissionsArgs): boolean => {
  let fragment: DocumentNode;

  switch (check) {
    case 'canAddPaidUser':
      fragment = CanAddPaidUserPermissionForAccount;
      break;
    case 'canAssignAdmin':
      fragment = CanAssignAdminPermissionForAccount;
      break;
    case 'canAssignOwner':
      fragment = CanAssignOwnerPermissionForAccount;
      break;
    case 'canCreateWorkspace':
      fragment = CanCreateWorkspacePermissionForAccount;
      break;
    case 'canDeleteUser':
      fragment = CanDeleteUserPermissionForAccount;
      break;
    case 'canManageAll':
      fragment = CanManageAllPermissionForAccount;
      break;
    case 'canManageBilling':
      fragment = CanManageBillingPermissionForAccount;
      break;
    case 'canManageResourceControlPolicies':
      fragment = CanManageResourceControlPoliciesForAccount;
      break;
    case 'canUpdateUserStatus':
      fragment = CanUpdateUserStatusPermissionForAccount;
      break;
    case 'canViewAllUsers':
      fragment = CanViewAllUsersPermissionForAccount;
      break;
    case 'canViewUserGroup':
      fragment = CanViewUserGroupPermissionForAccount;
      break;
    case 'canAccessMountedStorageFeature':
      fragment = IsMountedStorageFeatureDisabledForAccount;
      break;
    case 'canAccessTranscriptionFeature':
      fragment = IsTranscriptionFeatureDisabledForAccount;
      break;
    case 'canAccessUploadLinksFeature':
      fragment = IsUploadLinksFeatureDisabledForAccount;
      break;
    default:
      throw new Error(`Unknown permissions check: ${check}`);
  }

  const fragmentNode = fragment.definitions[0] as FragmentDefinitionNode;

  const result = useFragment({
    fragment,
    fragmentName: fragmentNode.name.value,
    from: {
      __typename: 'Account',
      id: accountId ?? '',
    },
  });

  // If the permission does not exist yet, we want to check against that rather than defaulting to false
  const isPermissionUnresolved =
    isNull(result) ||
    Object.keys(result?.data)?.length < 1 ||
    !result?.complete;

  /**
   * Return the result of the fragment, and fall back to the prop that controls whether we consider the permission true
   * prior to the permission being resolved
   */
  if (isPermissionUnresolved) {
    return isInitiallyPermitted;
  }

  // Check if this is a disabled feature access check
  if (check in DISABLED_FEATURE_MAP) {
    return checkDisabledFeatureAccess(check as DisabledFeatureKey, result);
  }

  // Regular permissions use the permissions object
  return result.data?.permissions?.[check as RegularPermissionKey];
};
